import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CalendarDays, ChevronLeft, ChevronRight, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import WeekGrid, { weekDates } from "@/components/WeekGrid";

export default function LearnerCalendar() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("upcoming");
  const [month, setMonth] = useState(new Date());
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  useEffect(() => { api.get("/learner/calendar").then(({ data }) => setEvents(data)); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const modifiers = { hasEvent: events.map(e => new Date(e.date)) };

  const token = localStorage.getItem("dec_learner_token");
  const icsUrl = `${API_BASE}/learner/calendar.ics?token=${token}`;

  const days = weekDates(weekAnchor);
  const weekLabel = `${days[0].toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;

  const copyIcs = async () => {
    await navigator.clipboard.writeText(icsUrl);
    toast.success("Lien copié — collez-le dans Google/Apple Calendar pour synchroniser.");
  };

  return (
    <div className="lg:ml-56">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl">Calendrier</h1>
          <p className="text-[#5C6561] mt-1 text-sm">Vos prochains rendez-vous.</p>
        </div>
        <div className="flex gap-2">
          <a href={icsUrl} download="decomplexe.ics">
            <Button variant="outline" className="dec-pill bg-white" data-testid="ical-download">
              <Download className="w-4 h-4 mr-1" /> Fichier .ics
            </Button>
          </a>
          <Button onClick={copyIcs} className="dec-pill bg-[#2A5B52] hover:bg-[#22493F] text-white" data-testid="ical-copy">
            <Copy className="w-4 h-4 mr-1" /> S'abonner (Google/Apple)
          </Button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-[#F4EFEA]">
          <TabsTrigger value="upcoming" data-testid="learner-view-list">Liste</TabsTrigger>
          <TabsTrigger value="week" data-testid="learner-view-week">Semaine</TabsTrigger>
          <TabsTrigger value="month" data-testid="learner-view-month">Mois</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "upcoming" && (
        <div className="mt-4 space-y-2">
          {upcoming.length === 0 && <div className="dec-card p-6 text-center text-[#5C6561]">Aucun cours à venir.</div>}
          {upcoming.map(e => (
            <div key={e.id} className="dec-card p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
              <div className="flex-1"><div className="font-semibold">{e.title}</div>
                <div className="text-xs text-[#5C6561]">{e.date} • {e.start_time} → {e.end_time}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "week" && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" className="bg-white" onClick={() => { const d = new Date(weekAnchor); d.setDate(d.getDate() - 7); setWeekAnchor(d); }} data-testid="learner-week-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="font-semibold text-[#1F2421] text-sm">{weekLabel}</div>
            <Button variant="outline" size="sm" className="bg-white" onClick={() => { const d = new Date(weekAnchor); d.setDate(d.getDate() + 7); setWeekAnchor(d); }} data-testid="learner-week-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <WeekGrid anchor={weekAnchor} events={events} />
        </div>
      )}

      {view === "month" && (
        <div className="mt-4 dec-card p-4">
          <CalendarPicker mode="single" month={month} onMonthChange={setMonth} modifiers={modifiers}
            modifiersClassNames={{ hasEvent: "bg-[#FDF0EC] text-[#D95D39] font-bold rounded-md" }} />
          <div className="mt-3 space-y-2">
            {events.filter(e => {
              const d = new Date(e.date);
              return d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear();
            }).map(e => (
              <div key={e.id} className="p-3 rounded-lg bg-[#F4EFEA] text-sm">
                <div className="font-semibold">{e.date} • {e.title}</div>
                <div className="text-xs text-[#5C6561]">{e.start_time}–{e.end_time}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
